// Pure, isomorphic Google Docs URL helpers (M22) — shared by the wizard's
// inline "is this pasteable?" validation (no network call, instant feedback)
// and the server fetcher's own re-validation (never trusts a client-computed
// docId). Safe for the models barrel: no Node builtins, no fetch, no I/O.

/** Google's file-id alphabet is base64url-ish; 20+ chars rules out doc-adjacent
 *  short paths (e.g. "/document/create") without over-fitting a fixed length. */
const DOC_ID_RE = /^[A-Za-z0-9_-]{20,}$/;
const DOC_PATH_RE =
  /^\/document\/(?:u\/\d+\/)?d\/([A-Za-z0-9_-]{20,})(?:[/?#]|$)/;
const SHEET_OR_SLIDES_PATH_RE = /^\/(spreadsheets|presentation)\//;

export type GoogleDocUrlClassification =
  | { kind: "doc"; docId: string }
  | { kind: "drive_file" }
  | { kind: "sheet_or_slides" }
  | { kind: "invalid" };

/** A scheme-less paste ("docs.google.com/document/d/...") is a normal thing
 *  to paste — prepend "https://" so `new URL()` accepts it rather than
 *  rejecting the whole link as invalid. */
function normalizeGoogleDocUrlInput(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return trimmed;
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/**
 * Classifies a pasted string with NO network call — the wizard uses this for
 * instant inline validation (Import disabled until `kind: "doc"`), and the
 * server fetcher re-runs it on the raw URL before ever making a request (a
 * client can't be trusted to have computed `docId` correctly). Distinguishes
 * "wrong Google product" (`drive_file`, `sheet_or_slides`) from a merely
 * malformed link (`invalid`) so the wizard can show tailored, specific copy
 * instead of one generic error.
 */
export function classifyGoogleDocUrl(raw: string): GoogleDocUrlClassification {
  const normalized = normalizeGoogleDocUrlInput(raw);
  if (!normalized) return { kind: "invalid" };

  let url: URL;
  try {
    url = new URL(normalized);
  } catch {
    return { kind: "invalid" };
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") {
    return { kind: "invalid" };
  }

  const host = url.hostname.toLowerCase();
  if (host === "drive.google.com") return { kind: "drive_file" };
  if (host !== "docs.google.com") return { kind: "invalid" };

  const docMatch = url.pathname.match(DOC_PATH_RE);
  if (docMatch) return { kind: "doc", docId: docMatch[1] };

  if (url.pathname === "/open") {
    const id = url.searchParams.get("id");
    if (id && DOC_ID_RE.test(id)) return { kind: "doc", docId: id };
    return { kind: "invalid" };
  }

  if (SHEET_OR_SLIDES_PATH_RE.test(url.pathname)) {
    return { kind: "sheet_or_slides" };
  }

  return { kind: "invalid" };
}

/** Convenience accessor over `classifyGoogleDocUrl` for callers that only
 *  care about the extracted id. */
export function extractGoogleDocId(raw: string): string | null {
  const classification = classifyGoogleDocUrl(raw);
  return classification.kind === "doc" ? classification.docId : null;
}

/**
 * The ONE place that builds the export URL the server actually fetches.
 * `format=docx` per the research (D-M22, no `format=html` fallback — real
 * named styles produce clean mammoth output; html would be a second,
 * lower-fidelity code path).
 */
export function googleDocExportUrl(docId: string): string {
  return `https://docs.google.com/document/d/${docId}/export?format=docx`;
}

/**
 * Terminal outcomes of a Google Docs fetch attempt (`google-doc-import.ts`).
 * The first three are re-validation failures on the URL itself (mirrors
 * `GoogleDocUrlClassification`, but as a fetch-result reason so the route can
 * respond uniformly regardless of whether the client's inline check was
 * bypassed); the rest are network/availability outcomes.
 */
export type GoogleDocFetchReason =
  | "invalid_url"
  | "drive_file_link"
  | "not_a_document"
  | "not_shared"
  | "not_found"
  | "too_large"
  | "rate_limited"
  | "fetch_failed";

/**
 * Friendly, per-reason copy — the ONE home for it (same anti-drift rule as
 * the import size-limit copy in template-editor.types.ts): the route returns
 * it in error bodies AND the wizard's inline pre-validation shows the same
 * words for the reasons it can detect client-side (invalid_url /
 * drive_file_link / not_a_document), so a bypassed client and an inline
 * rejection read identically. Every server reason maps to the SAME 422 —
 * the wizard reads the body's `error` copy, not the status code.
 */
export const GOOGLE_DOC_ERROR_COPY: Record<GoogleDocFetchReason, string> = {
  invalid_url: "That doesn't look like a Google Docs link.",
  drive_file_link:
    "That's a Drive file link, not a Google Doc. If it's a Word document, download it and upload the .docx instead.",
  not_a_document:
    "That link is a Google Sheet or Slides file — only Google Docs are supported.",
  not_shared: "We couldn't open that doc — it looks private.",
  not_found: "We couldn't find that document — double-check the link.",
  too_large:
    "That document is over 10MB — download it as a .docx and upload it instead, or split it into smaller docs.",
  rate_limited: "Google asked us to slow down — try again in a minute.",
  fetch_failed:
    "We couldn't reach Google Docs. Try again, or download the doc as a .docx and upload it instead.",
};

/**
 * The inverse of GOOGLE_DOC_ERROR_COPY — recovers the typed reason from the
 * copy the route puts in an error body's `error` field, so the import wizard
 * can report a stable analytics bucket (e.g. "not_shared") without the route
 * shipping a second wire field alongside it. Derived from the map above, so a
 * copy edit moves both directions at once; the only way to desync is two
 * reasons sharing identical copy, which the round-trip test pins.
 *
 * A miss is expected and meaningful: 422s from the shared conversion pipeline
 * carry their own message, so callers fall back rather than mislabel them.
 */
export const GOOGLE_DOC_REASON_BY_COPY = Object.fromEntries(
  Object.entries(GOOGLE_DOC_ERROR_COPY).map(([reason, copy]) => [copy, reason])
) as Record<string, GoogleDocFetchReason>;
